'use client';

import {
  BookOpenIcon,
  ChevronDown,
  ChevronUpIcon,
  CircleHelpIcon,
  LogOutIcon,
  PlusIcon,
  SettingsIcon,
  UserIcon
} from 'lucide-react';
import Link from 'next/link';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/shadcn/dropdown-menu';
import CreateTeamDialog from './CreateTeamDialog';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer
} from '../catalyst/sidebar';
import { useAccountNavData } from './useAccountNavData';
import { Button } from '../ui/Button';
import { cn } from '@/utils/cn';

export default function MobileAccountNav() {
  const {
    user,
    signOut,
    openUserProfile,
    workspace,
    inactiveWorkspaces,
    teamCount,
    isLoaded,
    activateWorkspace,
    createTeamOpen,
    setCreateTeamOpen,
    pathname,
    primaryNav,
    recentInterviews
  } = useAccountNavData();

  // Interviews on its own has nothing to be distinguished from, so the active
  // treatment only earns its place once the group holds more than one link.
  const showActiveNav = primaryNav.length > 1;
  const settingsCurrent = pathname.startsWith('/account/settings');

  if (!isLoaded) return null;
  if (!user) return null;

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="mb-4 flex w-full justify-center">
          <div className="flex w-full flex-row items-center justify-center">
            <div className="flex-1">
              <Link href="/account">
                <img
                  src="/brand/logo.svg"
                  alt="Corvus"
                  className="my-2 ml-1 h-[24px]"
                />
              </Link>
            </div>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarItem className="mb-2.5 rounded-lg border border-border bg-background shadow-sm">
              <SidebarLabel className="pl-2">{workspace?.name}</SidebarLabel>
              <ChevronDown className="ml-auto mr-2 size-4 text-muted-foreground" />
            </SidebarItem>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-64" align="start">
            {(inactiveWorkspaces ?? []).map((ws) => (
              <DropdownMenuItem
                onClick={() => activateWorkspace(ws)}
                key={ws.id}
              >
                {ws.name}
                {ws.personal && (
                  <>
                    {' '}
                    <UserIcon className="inline text-foreground" size={14} />
                  </>
                )}
              </DropdownMenuItem>
            ))}
            {(inactiveWorkspaces?.length ?? 0) > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={() => setCreateTeamOpen(true)}>
              <PlusIcon className="mr-2 text-muted-foreground" size={14} />
              <span className="text-foreground">Create Team</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarHeader>
      <SidebarBody>
        <SidebarSection>
          <Link href="/account/interview/add">
            <Button>
              <PlusIcon className="inline text-muted-foreground" size={14} />
              Create Interview
            </Button>
          </Link>
        </SidebarSection>
        <SidebarSection>
          {primaryNav.map((link) => {
            const current = showActiveNav && pathname.startsWith(link.href);
            return (
              <SidebarItem href={link.href} current={current} key={link.href}>
                <link.icon
                  className={cn(
                    current ? 'text-foreground' : 'text-muted-foreground'
                  )}
                  size={18}
                  strokeWidth={current ? 2.5 : 2}
                />
                <SidebarLabel>{link.label}</SidebarLabel>
              </SidebarItem>
            );
          })}
        </SidebarSection>
        {recentInterviews.length > 0 && (
          <SidebarSection>
            <SidebarHeading>Recent Interviews</SidebarHeading>
            {recentInterviews.map((interview) => (
              <SidebarItem
                key={interview.id}
                href={`/account/interview/${interview.slug}`}
                current={pathname === `/account/interview/${interview.slug}`}
              >
                <SidebarLabel>{interview.name}</SidebarLabel>
              </SidebarItem>
            ))}
          </SidebarSection>
        )}
        <SidebarSpacer />
        <SidebarSection>
          <SidebarItem href="/docs">
            <BookOpenIcon className="text-muted-foreground" size={18} />
            <SidebarLabel>Docs</SidebarLabel>
          </SidebarItem>
          <SidebarItem href="mailto:help@meetcorvus.ai">
            <CircleHelpIcon className="text-muted-foreground" size={18} />
            <SidebarLabel>Support</SidebarLabel>
          </SidebarItem>
          {/* Billing, API keys, and Branding are reached from inside
              Settings, which keeps this section to two entries. */}
          {!workspace?.personal && (
            <SidebarItem href="/account/settings" current={settingsCurrent}>
              <SettingsIcon
                className={cn(
                  settingsCurrent ? 'text-foreground' : 'text-muted-foreground'
                )}
                size={18}
                strokeWidth={settingsCurrent ? 2.5 : 2}
              />
              <SidebarLabel>Settings</SidebarLabel>
            </SidebarItem>
          )}
        </SidebarSection>
      </SidebarBody>
      <SidebarFooter>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarItem>
              <span className="flex min-w-0 items-center gap-3">
                <span className="min-w-0">
                  <span className="block truncate text-xs/5 font-normal text-muted-foreground">
                    {user.primaryEmailAddress?.emailAddress}
                  </span>
                </span>
              </span>
              <div className="ml-auto">
                <ChevronUpIcon />
              </div>
            </SidebarItem>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-64" side="top" align="start">
            <DropdownMenuItem onClick={() => openUserProfile()}>
              <UserIcon className="text-muted-foreground" size={16} />
              <span className="text-foreground">My profile</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut({ redirectUrl: '/' })}>
              <LogOutIcon className="mr-2 text-muted-foreground" size={16} />
              <span className="text-foreground">Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
      <CreateTeamDialog
        isOpen={createTeamOpen}
        setIsOpen={setCreateTeamOpen}
        currentTeamCount={teamCount}
      />
    </Sidebar>
  );
}
